import MuiTable from "@mui/material/Table";
import MuiTableBody from "@mui/material/TableBody";
import MuiTableCell from "@mui/material/TableCell";
import MuiTableContainer, { TableContainerProps as MuiTableContainerProps } from "@mui/material/TableContainer";
import MuiTableHead from "@mui/material/TableHead";
import MuiTableRow from "@mui/material/TableRow";
import MuiSkeleton from "@mui/material/Skeleton";
import { SxProps, Theme } from "@mui/material/styles";
import EmptyView from "../empty-view/empty-view";
import { useColumnDrag } from "./use-column-drag";
import { useTableFocus } from "./use-table-focus";
import { useSticky } from "./use-sticky";
import { fluxTheme } from "../theme";
export type TableCellValue = string | number | object | undefined;

export interface TableColumn<TRow> {
  field: string;
  value: string;
  width?: number;
  renderCell?: (row: TRow) => React.ReactNode;
  height?: string | number;
  sx?: React.CSSProperties;
  sticky?: "left" | "right";
  backgroundColor?: string;
}

export interface TableProps<TRow> extends MuiTableContainerProps {
  columns: readonly TableColumn<TRow>[];
  rows: TRow[] | undefined;
  handleRowClick?: (id: number) => void;
  isLoading?: boolean;
  onDropColumn?: (sourceIndex: number, targetIndex: number) => void;
  tableId?: string;
  stickyHeaders?: boolean;
  height?: string | number; // This is necessary for sticky headers to work.
  allowFocusOnRows?: boolean; // This allows you to tab to that row.
  ignoreFocusChildElements?: boolean; // Only relevant when allowFocusOnRows is true. If true, the enter and arrow keys will work to switch focus to other rows when focus is on an interactive element like a button inside the row.
  dataDogIdentifier: string;
  skeletonColumns?: number;
  displayRowLines?: boolean;
}

const Table = <TRow extends { id: number }>({
  columns,
  rows,
  handleRowClick,
  isLoading = false,
  onDropColumn,
  stickyHeaders = false,
  ignoreFocusChildElements = false,
  allowFocusOnRows = false,
  height,
  dataDogIdentifier,
  skeletonColumns = 3,
  displayRowLines = false
}: TableProps<TRow>): React.ReactElement => {
  const { handlers, getCellStyles } = useColumnDrag({ onDrop: onDropColumn });
  const { handleDragStart, handleDragOver, handleDragLeave, handleDrop } = handlers;
  const { rowRefs, handleFocusRow, tableRef, setIsTableFocused, handleKeyDown, getFocusedCellStyles } = useTableFocus(
    rows?.length ?? 0,
    ignoreFocusChildElements,
    allowFocusOnRows
  );

  const { getStickyStyles, columnRefs } = useSticky(columns);

  const showData = !isLoading && rows;
  const noData = !isLoading && (!rows || rows.length === 0);

  return noData ? (
    <EmptyView description="No data to display" />
  ) : (
    <MuiTableContainer
      sx={{
        border: `1px solid`,
        borderColor: fluxTheme.palette.other.tableRowGray,
        borderRadius: "10px",
        ...(height
          ? {
              maxHeight: height,
              overflow: "scroll"
            }
          : {}),
        ...(stickyHeaders
          ? {
              position: "relative",
              zIndex: 100
            }
          : {})
      }}
    >
      <MuiTable
        component="table"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsTableFocused(true)}
        ref={tableRef}
      >
        <MuiTableHead>
          {isLoading ? (
            <MuiTableRow>
              {Array.from({ length: columns.length }).map((_, i) => (
                <MuiTableCell
                  key={i}
                  sx={{
                    ...(displayRowLines && {
                      borderBottom: "1.5px solid",
                      borderColor: fluxTheme.palette.other.divider2
                    })
                  }}
                >
                  <MuiSkeleton variant="text" width={columns[i].width} />
                </MuiTableCell>
              ))}
            </MuiTableRow>
          ) : (
            <MuiTableRow data-action-name={`${dataDogIdentifier} table`}>
              {columns.map((column, index) => (
                <MuiTableCell
                  key={column.field}
                  width={column.width || 150}
                  draggable={!!onDropColumn}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => {
                    handleDrop(e, index);
                  }}
                  ref={(el: HTMLTableCellElement | null) => {
                    columnRefs.current[index] = el;
                  }}
                  sx={
                    {
                      padding: "16px",
                      textTransform: "uppercase",
                      ...getStickyStyles(column, index, stickyHeaders),
                      ...getCellStyles(index),
                      ...(column?.backgroundColor ? { backgroundColor: column.backgroundColor } : {}),
                      ...(displayRowLines && {
                        borderBottom: "1.5px solid",
                        borderColor: fluxTheme.palette.other.divider2
                      })
                    } as SxProps<Theme>
                  }
                >
                  {column.value}
                </MuiTableCell>
              ))}
            </MuiTableRow>
          )}
        </MuiTableHead>
        {isLoading && (
          <MuiTableBody>
            {Array.from({ length: skeletonColumns }).map((_, i) => (
              <MuiTableRow
                key={i}
                sx={{
                  ...(displayRowLines &&
                    i !== skeletonColumns - 1 && {
                      borderBottom: "1.5px solid",
                      borderColor: fluxTheme.palette.other.divider2
                    })
                }}
              >
                {Array.from({ length: columns.length }).map((_, j) => (
                  <MuiTableCell key={j}>
                    <MuiSkeleton variant="text" width={columns[j].width} />
                  </MuiTableCell>
                ))}
              </MuiTableRow>
            ))}
          </MuiTableBody>
        )}
        {showData && (
          <MuiTableBody>
            {rows.map((row, index) => (
              <MuiTableRow
                onKeyDown={handleKeyDown}
                onFocus={(e) => {
                  const mouseEvent = e as unknown as React.MouseEvent;
                  handleFocusRow(index, mouseEvent);
                }}
                data-cy="table-row"
                key={row.id}
                ref={(el: HTMLTableRowElement | null) => {
                  rowRefs.current[index] = el;
                }}
                tabIndex={0}
                onClick={(e) => {
                  if (handleRowClick) {
                    handleRowClick(row.id);
                  }
                  handleFocusRow(index, e);
                }}
                sx={{
                  "&:hover > td": {
                    backgroundColor: "action.hover"
                  },
                  ...(displayRowLines &&
                    index !== rows.length - 1 && {
                      borderBottom: "1.5px solid",
                      borderColor: fluxTheme.palette.other.divider2
                    }),
                  cursor: handleRowClick ? "pointer" : "default"
                }}
                data-action-name={`${dataDogIdentifier} table row`}
              >
                {columns.map((col, colIndex) => {
                  const val = (row as unknown as Record<string, TableCellValue>)[col.field];
                  // TODO: We can't render any arbitrary object here, so we'll just render null for now and expect a custom renderCell function
                  const value = typeof val === "object" ? null : val;
                  const cellContent = col?.renderCell ? col.renderCell(row) : value;

                  return (
                    <MuiTableCell
                      key={col.field}
                      ref={(el: HTMLTableCellElement | null) => {
                        columnRefs.current[colIndex] = el;
                      }}
                      sx={{
                        ...getStickyStyles(col, colIndex),
                        ...(col?.backgroundColor ? { backgroundColor: col.backgroundColor } : {}),
                        ...getFocusedCellStyles(index)
                      }}
                    >
                      <>{cellContent}</>
                    </MuiTableCell>
                  );
                })}
              </MuiTableRow>
            ))}
          </MuiTableBody>
        )}
      </MuiTable>
    </MuiTableContainer>
  );
};

export default Table;
